import { apiClient } from './api-client'
import { InboxFilters, toApiParams } from '@/lib/publication-inbox'

export type InboxState = 'NOVA' | 'EM_TRIAGEM' | 'PRAZO_LANCADO' | 'SEM_PROVIDENCIA' | 'CONFERIDA'

export interface InboxItem {
  id: number
  dataDisponibilizacao: string
  siglaTribunal: string
  tipoComunicacao: string
  numeroProcesso: string
  nomeOrgao: string
  resumo: string
  estado: InboxState
  analysisStatus: string
  /** ISO 8601; null quando não conferida. */
  conferidaEm: string | null
  prioridade: string | null
  responsavel: { id: string; nome: string | null } | null
  caso: { id: string; numeroInterno: string | null } | null
  advogados: Array<{ nome: string; numeroOab: string; ufOab: string }>
}

export interface InboxPage {
  items: InboxItem[]
  total: number
  page: number
  limit: number
  totalPages: number
  counts: Record<InboxState, number>
}

/** Resultado de uma ação em lote (POST /api/publications/bulk-actions; 207 = falha parcial). */
export interface BulkResult {
  ok: number[]
  falhas: Array<{ id: number; error: string }>
}

/** Caixa de entrada de publicações (GET /api/publications/inbox). */
export const publicationInboxService = {
  async list(filters: InboxFilters): Promise<InboxPage> {
    const response = await apiClient.get('/api/publications/inbox', { params: toApiParams(filters) })
    return response.data.data
  },

  /** Publicações novas de um advogado (`todos`, `eu` ou id): total e as mais recentes. */
  async novas(advogado: string, limit = 3): Promise<Pick<InboxPage, 'items' | 'total'>> {
    const response = await apiClient.get('/api/publications/inbox', { params: { advogado, estado: 'NOVA', page: 1, limit } })
    const page: InboxPage = response.data.data
    return { items: page.items, total: page.total }
  },

  /** Marca como conferida (lida, nada a fazer): sai de Nova/Em triagem. Idempotente. */
  async conferir(id: number): Promise<void> {
    await apiClient.post(`/api/publications/${id}/conferir`)
  },

  async desconferir(id: number): Promise<void> {
    await apiClient.post(`/api/publications/${id}/desconferir`)
  },

  /** Até 100 ids por chamada (limite da API). */
  async emLote(action: 'conferir' | 'desconferir', ids: number[]): Promise<BulkResult> {
    const response = await apiClient.post('/api/publications/bulk-actions', { action, publicationIds: ids })
    const data = response.data?.data ?? {}
    return {
      ok: (data.results ?? []).map((r: { id: number }) => r.id),
      falhas: data.errors ?? [],
    }
  },
}
